import type { HTMLAttributes } from 'react'
import { cx } from './cx'
import { Button } from './Button'
import s from './ErrorState.module.css'

/**
 * The honest failure surface — shown when a load ERRORS (distinct from EmptyState,
 * which means "loaded, nothing here"). A quiet icon + message + a Retry, so a
 * transient outage reads as recoverable, not as "there's no data". Every live-read
 * surface uses this instead of collapsing to an empty state on error.
 */
export interface ErrorStateProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: string
  message?: string
  onRetry?: () => void
  retryLabel?: string
}

/* REST PROPS REACH THE ELEMENT, and `data-testid` is DEFAULTED rather than
   fixed (#2406). The hardcoded `error-state` was the only testid a host could
   get, so a surface with its own — `dup-error`, `response-error`,
   `lifecycle-error` — could not adopt this component without losing the
   locator its spec uses. Spreading `rest` AFTER the default lets a host
   override it and leaves every existing caller on `error-state`. */
export function ErrorState({
  title = 'Couldn’t load this', message, onRetry, retryLabel = 'Retry', className, ...rest
}: ErrorStateProps) {
  return (
    <div className={cx(s.wrap, className)} role="alert" data-testid="error-state" {...rest}>
      <span className={s.glyph} aria-hidden="true">⚠</span>
      <div className={s.title}>{title}</div>
      {message && <div className={s.msg}>{message}</div>}
      {onRetry && <Button size="sm" variant="secondary" onClick={onRetry} data-testid="error-retry">{retryLabel}</Button>}
    </div>
  )
}
